import React, { useState, useEffect } from 'react';
import { ShieldCheck, Lock, Save, Loader2, ChevronDown, ChevronUp, Share2 } from 'lucide-react';
import { api } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import ShareFieldDialog from './ShareFieldDialog';

/**
 * Personal Profile section for the Settings page. All values are encrypted
 * server-side. UI shows a completeness % to nudge users to fill missing
 * required fields.
 */
const FIELDS = [
  { group: 'Identity', items: [
    { key: 'dob', label: 'Date of birth', type: 'date', required: true },
    { key: 'ni_number', label: 'National Insurance number', placeholder: 'AB 12 34 56 C', required: true },
    { key: 'tax_number', label: 'Tax / UTR number', placeholder: '10-digit reference', required: true },
  ]},
  { group: 'Travel document', items: [
    { key: 'passport_number', label: 'Passport number', placeholder: '9 alphanumeric characters', required: true },
    { key: 'passport_expiry', label: 'Passport expiry', type: 'date', required: true },
  ]},
  { group: 'Contact & Address', items: [
    { key: 'phone', label: 'Phone number', placeholder: '+44 ...', required: true },
    { key: 'address_line1', label: 'Address line 1', required: true },
    { key: 'address_line2', label: 'Address line 2 (optional)' },
    { key: 'city', label: 'City' },
    { key: 'postcode', label: 'Postcode', required: true },
    { key: 'country', label: 'Country', required: true },
  ]},
  { group: 'Professional (optional)', items: [
    { key: 'company_name', label: 'Company name' },
    { key: 'insurance_broker_ref', label: 'Insurance broker reference' },
  ]},
];

export const PersonalProfileSection = () => {
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.getMyProfile().then(r => {
      setProfile(r.data);
      const f = {};
      Object.keys(r.data).forEach(k => {
        if (typeof r.data[k] === 'string') f[k] = r.data[k];
      });
      setForm(f);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.updateMyProfile(form);
      setProfile(r.data);
      toast.success(`Profile saved · ${r.data.completeness_pct}% complete`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return (
      <div className="luxury-card p-8 mb-6">
        <div className="h-4 w-32 bg-white/5 rounded animate-pulse" />
      </div>
    );
  }

  const pct = profile.completeness_pct ?? 0;

  return (
    <div className="luxury-card p-8 mb-6" data-testid="personal-profile-section" id="profile">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <ShieldCheck size={20} className="text-gold" />
          <h2 className="font-serif text-xl text-white">Personal Profile</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{pct}% complete</span>
          <button onClick={() => setOpen(!open)} className="text-muted-foreground hover:text-white" data-testid="toggle-profile-section">
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      <div className="h-1 bg-[#1A1A1A] rounded-full overflow-hidden mb-5">
        <div
          className={`h-full transition-all ${pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-gold' : 'bg-amber-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {open && (
        <>
          <div className="p-3 bg-emerald-500/5 border border-emerald-500/20 rounded mb-3 flex items-start gap-2">
            <Lock size={12} className="text-emerald-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-emerald-200/80 leading-relaxed">
              All fields below are encrypted at rest with AES-256 and never leave Assagart. You can grant
              your insurance broker, accountant or wealth manager a one-time secure view of selected
              fields using the button below - they never see anything you have not explicitly shared.
            </p>
          </div>

          <div className="mb-5">
            <Button
              type="button"
              onClick={() => setShareOpen(true)}
              className="bg-gold/10 hover:bg-gold/20 text-gold border border-gold/30 hover:border-gold/50 transition-colors w-full sm:w-auto"
              data-testid="share-fields-btn"
            >
              <Share2 size={14} className="mr-2" /> Share fields with a broker / advisor
            </Button>
          </div>

          <ShareFieldDialog open={shareOpen} onClose={() => setShareOpen(false)} />

          {FIELDS.map(group => (
            <div key={group.group} className="mb-6">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-3">{group.group}</p>
              <div className="grid md:grid-cols-2 gap-4">
                {group.items.map(field => (
                  <div key={field.key}>
                    <label className="text-xs text-muted-foreground flex items-center gap-1">
                      {field.label}
                      {field.required && <span className="text-amber-400">*</span>}
                    </label>
                    <Input
                      type={field.type || 'text'}
                      value={form[field.key] || ''}
                      onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                      placeholder={field.placeholder || ''}
                      className="luxury-input mt-1"
                      data-testid={`profile-${field.key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="flex items-center justify-between pt-4 border-t border-[#1A1A1A]">
            {profile.last_updated_at ? (
              <p className="text-[10px] text-muted-foreground">Last updated {new Date(profile.last_updated_at).toLocaleString('en-GB')}</p>
            ) : <span />}
            <Button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-profile-btn">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save profile
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
